// Auth / onboarding funnel mirror — called from `onEvent` for every analytics
// event, forwards only the allowlisted ones (see the contract) to the
// same-origin `/api/telemetry-beacon` route, which turns them into OTel
// counters. sendBeacon so the sample survives navigation away from the page
// (a successful login navigates immediately) and ad-blockers.

import { FUNNEL_BEACON_EVENT, FUNNEL_MIRRORS, type FunnelMirror } from './funnel-beacon-contract';

const ENDPOINT = '/api/telemetry-beacon';

/** Only strings and booleans become labels; anything else is dropped here and
 *  defaulted server-side, so a stray number or object never widens cardinality. */
function pickLabels(
  mirror: FunnelMirror,
  properties: Readonly<Record<string, unknown>>,
): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const sourceKey of Object.keys(mirror.labels)) {
    // ALLOW-UNKNOWN: analytics payloads are untyped; only string/boolean values become labels.
    const value: unknown = properties[sourceKey];
    if (typeof value === 'string') labels[sourceKey] = value;
    else if (typeof value === 'boolean') labels[sourceKey] = value ? 'true' : 'false';
  }
  return labels;
}

export function reportFunnelEvent(name: string, properties: object): void {
  const mirror: FunnelMirror | undefined = FUNNEL_MIRRORS[name];
  if (mirror === undefined) return;
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
  try {
    // ALLOW-UNKNOWN: registry payloads are plain objects; only allowlisted keys are read.
    const labels: Record<string, string> = pickLabels(
      mirror,
      properties as Readonly<Record<string, unknown>>,
    );
    const blob: Blob = new Blob([JSON.stringify({ event: FUNNEL_BEACON_EVENT, name, labels })], {
      type: 'application/json',
    });
    navigator.sendBeacon(ENDPOINT, blob);
  } catch {
    // SILENT-CATCH: telemetry must never crash the page.
  }
}
